// Enums

export const VolunteerSignUpStatus = {
  Confirmed: 'confirmed',
  Cancelled: 'cancelled',
} as const

export type VolunteerSignUpStatus =
  (typeof VolunteerSignUpStatus)[keyof typeof VolunteerSignUpStatus]

export const VolunteerQualificationType = {
  Boolean: 'boolean',
  SingleChoice: 'single_choice',
  MultiChoice: 'multi_choice',
  Text: 'text',
} as const

export type VolunteerQualificationType =
  (typeof VolunteerQualificationType)[keyof typeof VolunteerQualificationType]

// Organizer resources

export interface VolunteerEditionSettings {
  edition_id: number
  volunteers_enabled: boolean
  volunteer_require_account: boolean
  public_url: string | null
}

export interface VolunteerSlot {
  id: number
  edition_id: number
  slot_date: string // 'YYYY-MM-DD'
  start_time: string // 'HH:MM' (seconds already stripped by backend)
  end_time: string // 'HH:MM'
  capacity: number
  confirmed_count?: number
  notes: string | null
  created_at: string | null
  updated_at: string | null
}

export interface VolunteerRole {
  id: number
  edition_id: number
  name: string
  description: string | null
  capacity: number | null
  confirmed_count?: number
  sort_order: number
  created_at: string | null
  updated_at: string | null
}

export interface VolunteerQualificationOption {
  id: number
  label: string
  sort_order: number
}

export interface VolunteerQualificationCategory {
  id: number
  edition_id: number
  name: string
  type: VolunteerQualificationType
  enabled: boolean
  sort_order: number
  options: VolunteerQualificationOption[]
  created_at: string | null
  updated_at: string | null
}

export interface VolunteerSignUpSlotSummary {
  id: number
  slot_date: string
  start_time: string
  end_time: string
}

export interface VolunteerSignUpRoleSummary {
  id: number
  name: string
}

export interface VolunteerSignUp {
  id: number
  reference_code: string
  first_name: string
  last_name: string
  email: string
  phone: string
  status: VolunteerSignUpStatus
  confirmed_at: string
  slots: VolunteerSignUpSlotSummary[]
  roles: VolunteerSignUpRoleSummary[]
  qualification_summary: string | null
  created_at: string | null
}

export interface VolunteerQualificationAnswer {
  category: {
    id: number
    name: string
    type: VolunteerQualificationType
  }
  boolean_value: boolean | null
  text_value: string | null
  options: Array<{ id: number; label: string }>
}

export interface VolunteerSignUpDetail extends VolunteerSignUp {
  emergency_contact_name: string | null
  emergency_contact_phone: string | null
  qualifications: VolunteerQualificationAnswer[]
}

// Schedule resource

export interface VolunteerScheduleVolunteer {
  sign_up_id: number
  first_name: string
  last_name: string
  reference_code: string
  roles: string[]
}

export interface VolunteerScheduleSlot {
  slot_id: number
  start_time: string
  end_time: string
  capacity: number
  volunteers: VolunteerScheduleVolunteer[]
}

export interface VolunteerScheduleDate {
  date: string
  slots: VolunteerScheduleSlot[]
}

export interface VolunteerSchedule {
  edition_id: number
  dates: VolunteerScheduleDate[]
}

// Pagination

export interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
  from: number | null
  to: number | null
}

export interface Paginated<T> {
  data: T[]
  meta: PaginationMeta
}

// Public resources

export interface PublicVolunteerSlot {
  id: number
  slot_date: string
  start_time: string
  end_time: string
  capacity: number
  confirmed_count: number
}

export interface PublicVolunteerRole {
  id: number
  name: string
  description: string | null
  capacity: number | null
  confirmed_count: number
  sort_order: number
}

export interface PublicVolunteerQualificationOption {
  id: number
  label: string
}

export interface PublicVolunteerQualification {
  id: number
  name: string
  type: VolunteerQualificationType
  options: PublicVolunteerQualificationOption[]
}

export interface PublicVolunteerInfo {
  event: {
    slug: string
    name: string
    organisation: {
      slug: string
      name: string
    }
  }
  edition: {
    name: string
    language: string
    volunteer_require_account: boolean
  }
  slots: PublicVolunteerSlot[]
  roles: PublicVolunteerRole[]
  qualifications: PublicVolunteerQualification[]
}

export interface PublicVolunteerSignUpSlot {
  slot_date: string
  start_time: string
  end_time: string
}

export interface PublicVolunteerSignUp {
  reference_code: string
  confirmation_token: string
  first_name: string
  email: string
  event: {
    slug: string
    name: string
  }
  slots: PublicVolunteerSignUpSlot[]
  roles: Array<{ name: string }>
}

// Wizard form data

export interface VolunteerQualificationPayload {
  category_id: number
  boolean_value?: boolean | null
  text_value?: string | null
  option_ids?: number[]
}

export interface VolunteerSignUpPayload {
  first_name: string
  last_name: string
  email: string
  phone: string
  emergency_contact_name?: string | null
  emergency_contact_phone?: string | null
  slot_ids: number[]
  role_ids: number[]
  qualifications?: VolunteerQualificationPayload[]
}

// Email-check response
export interface VolunteerEmailCheckResult {
  found: boolean
  name?: string
  account_required: boolean
}
